import type { Object3D } from 'three';
import { CanvasTexture, Group, Mesh, MeshBasicMaterial, PlaneGeometry, SRGBColorSpace } from 'three';

import { DROP_INDICATOR_TYPE } from './drop-indicator.cfn';

const STRIP_THICKNESS_IN = 0.6;
const TICK_LENGTH_IN = 4;
const STRIP_COLOR = 0xffd36b;
const CANVAS_W = 256;
const CANVAS_H = 108;
const FONT = '600 64px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/** Inches to the nearest sixteenth, as a fraction: 4.5 → 4½", 0.375 → ⅜", 7 → 7". */
export const formatInchesSixteenthsCfn = (inches: number): string => {
	const total = Math.round(Math.abs(inches) * 16);
	const whole = Math.floor(total / 16);
	const sixteenths = total % 16;
	const glyphs: Record<number, string> = { 2: '⅛', 4: '¼', 6: '⅜', 8: '½', 10: '⅝', 12: '¾', 14: '⅞' };
	// a glyph sits against the whole number (4½); an odd sixteenth is spelled out with a space (1 9/16)
	const glyph = glyphs[sixteenths];
	const fraction = sixteenths === 0 ? '' : (glyph ?? `${sixteenths}/16`);
	const body = whole === 0 && fraction ? fraction : (glyph || !fraction ? `${whole}${fraction}` : `${whole} ${fraction}`);
	return `${body}"`;
};

interface SideObjects {
	strip: Mesh<PlaneGeometry, MeshBasicMaterial>;
	tickA: Mesh<PlaneGeometry, MeshBasicMaterial>;
	tickB: Mesh<PlaneGeometry, MeshBasicMaterial>;
	label: Mesh<PlaneGeometry, MeshBasicMaterial>;
	canvas: HTMLCanvasElement;
	texture: CanvasTexture;
	text: string | null;
}

/**
 * Spacing lines printed on the floor while a column is fine-tuned: on each side
 * a line from the column's edge to its neighbour's edge (or the panel's end),
 * with end ticks and the distance as a fraction of an inch. Parented to the
 * island, in its local x, so the lines sit along the panel whichever way it faces.
 */
export interface DimensionOverlay {
	group: Group;
	left: SideObjects;
	right: SideObjects;
	labelHeightIn: number;
}

/** Island-local x range of one side, `from` < `to`. */
export interface DimensionSpan {
	from: number;
	to: number;
}

const flat = <T extends Mesh>(mesh: T): T => {
	mesh.rotation.x = -Math.PI / 2;
	mesh.userData = { type: DROP_INDICATOR_TYPE };
	mesh.renderOrder = 19;
	return mesh;
};

const makeSide = (stripMaterial: MeshBasicMaterial, labelWidthIn: number, labelHeightIn: number): SideObjects => {
	const canvas = document.createElement('canvas');
	canvas.width = CANVAS_W;
	canvas.height = CANVAS_H;
	const texture = new CanvasTexture(canvas);
	texture.colorSpace = SRGBColorSpace;
	const labelMaterial = new MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false, toneMapped: false });
	const label = flat(new Mesh(new PlaneGeometry(labelWidthIn, labelHeightIn), labelMaterial));
	label.renderOrder = 20;
	return {
		strip: flat(new Mesh(new PlaneGeometry(1, STRIP_THICKNESS_IN), stripMaterial)),
		tickA: flat(new Mesh(new PlaneGeometry(STRIP_THICKNESS_IN, TICK_LENGTH_IN), stripMaterial)),
		tickB: flat(new Mesh(new PlaneGeometry(STRIP_THICKNESS_IN, TICK_LENGTH_IN), stripMaterial)),
		label,
		canvas,
		texture,
		text: null
	};
};

const drawLabel = (side: SideObjects, text: string): void => {
	const ctx = side.canvas.getContext('2d');
	if (!ctx) return;
	ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
	ctx.font = FONT;
	const width = Math.min(CANVAS_W - 8, ctx.measureText(text).width + 48);
	const x0 = (CANVAS_W - width) / 2;
	const r = CANVAS_H / 2 - 6;
	ctx.beginPath();
	ctx.moveTo(x0 + r, 6);
	ctx.lineTo(x0 + width - r, 6);
	ctx.arcTo(x0 + width, 6, x0 + width, 6 + r, r);
	ctx.arcTo(x0 + width, CANVAS_H - 6, x0 + width - r, CANVAS_H - 6, r);
	ctx.lineTo(x0 + r, CANVAS_H - 6);
	ctx.arcTo(x0, CANVAS_H - 6, x0, CANVAS_H - 6 - r, r);
	ctx.arcTo(x0, 6, x0 + r, 6, r);
	ctx.closePath();
	ctx.fillStyle = 'rgba(18, 22, 30, 0.86)';
	ctx.fill();
	ctx.lineWidth = 3;
	ctx.strokeStyle = 'rgba(255, 211, 107, 0.7)';
	ctx.stroke();
	ctx.fillStyle = '#ffffff';
	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';
	ctx.fillText(text, CANVAS_W / 2, (CANVAS_H / 2) + 3, CANVAS_W - 56);
	side.texture.needsUpdate = true;
	side.text = text;
};

const setSideVisible = (side: SideObjects, visible: boolean): void => {
	side.strip.visible = visible;
	side.tickA.visible = visible;
	side.tickB.visible = visible;
	side.label.visible = visible;
};

/** A hidden overlay; null where there is no DOM. */
export const createDimensionOverlayCfn = (labelWidthIn: number, labelHeightIn: number): DimensionOverlay | null => {
	if (typeof document === 'undefined') return null;
	const stripMaterial = new MeshBasicMaterial({ color: STRIP_COLOR, transparent: true, opacity: 0.95, depthWrite: false, toneMapped: false });
	const group = new Group();
	group.name = 'appliance-drag-dimensions';
	group.userData = { type: DROP_INDICATOR_TYPE, id: 'drag-dimensions' };
	group.visible = false;
	const left = makeSide(stripMaterial, labelWidthIn, labelHeightIn);
	const right = makeSide(stripMaterial, labelWidthIn, labelHeightIn);
	for (const side of [left, right]) group.add(side.strip, side.tickA, side.tickB, side.label);
	return { group, left, right, labelHeightIn };
};

const placeSide = (overlay: DimensionOverlay, side: SideObjects, span: DimensionSpan | null, outwardSign: 1 | -1): void => {
	const length = span ? span.to - span.from : 0;
	if (!span || length < 0.03) {
		setSideVisible(side, false);
		return;
	}
	const mid = (span.from + span.to) / 2;
	side.strip.scale.x = length;
	side.strip.position.set(mid, 0, 0);
	side.tickA.position.set(span.from, 0, 0);
	side.tickB.position.set(span.to, 0, 0);
	// the number sits just behind the line, toward the island, and reads from the front
	side.label.position.set(mid, 0.05, -outwardSign * ((overlay.labelHeightIn / 2) + 1.2));
	side.label.rotation.set(-Math.PI / 2, 0, outwardSign === 1 ? 0 : Math.PI);
	const text = formatInchesSixteenthsCfn(length);
	if (side.text !== text) drawLabel(side, text);
	setSideVisible(side, true);
};

/** Lay the lines on the island's floor, `offsetIn` out from the front face, for the room on each side of the column. */
export const placeDimensionOverlayCfn = (
	overlay: DimensionOverlay,
	island: { group: Object3D; frontZ: number; outwardSign: 1 | -1; floorY: number },
	offsetIn: number,
	liftIn: number,
	spans: { left: DimensionSpan | null; right: DimensionSpan | null }
): void => {
	const { group } = overlay;
	if (group.parent !== island.group) island.group.add(group);
	group.position.set(0, island.floorY + liftIn, island.frontZ + (island.outwardSign * offsetIn));
	placeSide(overlay, overlay.left, spans.left, island.outwardSign);
	placeSide(overlay, overlay.right, spans.right, island.outwardSign);
	group.visible = true;
};

export const disposeDimensionOverlayCfn = (overlay: DimensionOverlay): void => {
	overlay.group.parent?.remove(overlay.group);
	for (const side of [overlay.left, overlay.right]) {
		side.strip.geometry.dispose();
		side.tickA.geometry.dispose();
		side.tickB.geometry.dispose();
		side.label.geometry.dispose();
		side.label.material.dispose();
		side.texture.dispose();
	}
	overlay.left.strip.material.dispose();
};
